import * as React from 'react'
import Box from '@mui/material/Box'
import makeStyles from '@mui/styles/makeStyles'
import { BackdropProgress } from '@shared/components/backdrop-progress.component'
import { useQuery } from 'react-query'
import {
  Location,
  Scope,
  BaseRequirement,
  FormattedBaseRequirementGroupedByScopes,
} from '@shared/types'
import { apiJSON } from '@shared/utils/api/api.util'
import {
  Autocomplete,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { orderBy } from 'lodash'
import { Dispatch, SetStateAction, useState } from 'react'
import { ProgressButton } from '@shared/components/progress-button.component'
import { GroupWithScopeAndRequirements } from '@shared/hooks/use-requirements-data.hook'
import { NO_OPTIONS_PLACEHOLDER } from '@shared/consts'

interface Props {
  scopes: Scope[]
  selected?: number[]
  groupsPerScopeValues:
    | GroupWithScopeAndRequirements<BaseRequirement>[][]
    | FormattedBaseRequirementGroupedByScopes[]
  setSelected: Dispatch<SetStateAction<number[]>>
  setDeselectedScopes?: Dispatch<
    SetStateAction<{ scopeId: number; excludedRequirements: number[] }[]>
  >
}

const endpoint = `companies`

export default function CompaniesSelectToRequirementsMappingComponent({
  scopes,
  groupsPerScopeValues,
  setSelected,
  selected,
  setDeselectedScopes,
}: Props) {
  const classes = useStyles()
  const [selectedCompany, setSelectedCompany] = useState<Location | null>(null)
  const [selectedScopes, setSelectedScopes] = useState<number[]>([])

  const [showNoRequirementsMessage, setShowNoRequirementMessage] =
    useState(false)
  const [isCopied, setIsCopied] = useState(false)

  const { data: companies, isLoading: areCompaniesLoading } = useQuery<
    Location[]
  >(endpoint, () => apiJSON(endpoint))
  const [isLoading, setIsLoading] = useState(false)

  const handleCopyRequirements = async () => {
    if (selectedCompany) {
      setIsLoading(true)
      const selectedCompanyFetchedRequirements = (await apiJSON(
        `CompaniesEvaluationProfiles/${selectedCompany.companyID}/publishedBaseRequirements`
      )) as number[]
      setIsLoading(false)

      const scopesList = selectedScopes.map((scope) => scope)

      const allowedGroups = groupsPerScopeValues
        .filter((groups) => scopesList.includes(groups[0].scope.scopeID))
        .flat()

      const groupRequirements = allowedGroups.flatMap((group) =>
        group.requirements.map((req) => req.requirementBaseID)
      )

      const selectedCompanyRequirements: number[] =
        selectedCompanyFetchedRequirements.filter((id: number) =>
          groupRequirements.includes(id)
        )

      const requirementsToAdd: number[] = selectedCompanyRequirements.filter(
        (id: number) => selectedCompanyRequirements.includes(id)
      )

      if (!requirementsToAdd.length) {
        setShowNoRequirementMessage(true)
        return
      } else {
        setShowNoRequirementMessage(false)
      }

      const scopesInEsqula = allowedGroups
        .map((group) => {
          const scopeId = group.scope.scopeID

          const excludedRequirements = group.requirements
            .map((req) => req.requirementBaseID)
            .filter((id) => !requirementsToAdd.includes(id))

          return excludedRequirements.length > 0
            ? { scopeId, excludedRequirements }
            : null
        })
        .filter(Boolean)

      const groupedScopes = scopesInEsqula.reduce<
        { scopeId: number; excludedRequirements: number[] }[]
      >((acc, item) => {
        if (!item) return acc

        const existingScope = acc.find(
          (scope) => scope.scopeId === item?.scopeId
        )

        if (existingScope) {
          existingScope.excludedRequirements = [
            ...Array.from(
              new Set([
                ...existingScope.excludedRequirements,
                ...item.excludedRequirements,
              ])
            ),
          ]
        } else {
          acc.push(item)
        }

        return acc
      }, [])

      const updatedSelected =
        selected?.filter((item) => !groupRequirements.includes(item)) || []

      setDeselectedScopes?.(groupedScopes)
      setSelected([...updatedSelected, ...requirementsToAdd])
      setIsCopied(true)
    }
  }

  const handleChange = (event: any) => {
    const {
      target: { value },
    } = event
    setSelectedScopes(typeof value === 'string' ? value.split(',') : value)
  }

  React.useEffect(() => {
    handleCopyRequirements()
  }, [scopes])

  React.useEffect(() => {
    setShowNoRequirementMessage(false)
    setIsCopied(false)
  }, [selectedCompany, selectedScopes])

  if (areCompaniesLoading) {
    return <BackdropProgress />
  }

  return (
    <Box className={classes.wrapper}>
      <h2 className={classes.header}>Skopiuj wymagania z innej lokalizacji</h2>
      <Autocomplete
        options={orderBy(companies, 'name')}
        getOptionLabel={(option) => option?.name || ''}
        fullWidth
        filterSelectedOptions
        onChange={(event, value) => {
          setSelectedCompany(value as Location | null)
        }}
        renderInput={(params) => (
          <Box className={classes.inputWrapper}>
            <TextField {...params} label="Lokalizacja" />
          </Box>
        )}
        noOptionsText={NO_OPTIONS_PLACEHOLDER}
      />

      {selectedCompany && (
        <>
          <p className={classes.nextLabel}>następnie</p>
          <FormControl sx={{ m: 1, width: 300 }}>
            <InputLabel id="choose-scopes-label">
              Wybierz zakresy do skopiowania
            </InputLabel>
            <Select
              labelId="choose-scopes-label"
              id="choose-scopes"
              multiple
              fullWidth
              sx={{ width: 850, marginLeft: '-8px' }}
              value={selectedScopes}
              onChange={handleChange}
              input={<OutlinedInput label="Wybierz zakresy do skopiowania" />}
              renderValue={(selected) =>
                scopes
                  .filter((scope) => selected.includes(scope.scopeID))
                  .map((scope) => scope.name)
                  .join(', ')
              }
            >
              {scopes.map((scope) => (
                <MenuItem key={scope.scopeID} value={scope.scopeID}>
                  <Checkbox checked={selectedScopes.includes(scope.scopeID)} />
                  <ListItemText primary={scope.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box className={classes.submitButton}>
            <ProgressButton
              onClick={handleCopyRequirements}
              isLoading={isLoading}
              variant="outlined"
              color="primary"
              className={classes.button}
              disabled={!selectedScopes.length}
            >
              {isCopied ? 'Skopiowano' : 'Zatwierdź'}
            </ProgressButton>
          </Box>
        </>
      )}
      {showNoRequirementsMessage && (
        <Typography sx={{ textAlign: 'center', marginTop: '15px' }}>
          Brak wymagań do skopiowania
        </Typography>
      )}
    </Box>
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    wrapper: {
      maxWidth: 850,
      margin: '0 auto',
    },
    header: {
      color: theme.palette.grey[600],
      textAlign: 'center',
      fontWeight: 300,
    },
    inputWrapper: {
      display: 'flex',
      flexDirection: 'row',
    },
    button: {
      marginLeft: theme.spacing(1),
    },
    nextLabel: {
      color: theme.palette.grey[600],
      textAlign: 'center',
      fontWeight: 400,
    },
    submitButton: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '15px',
    },
  }
})
